/**
 * New Year countdown cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearCountdownCardsPack: ToilePack = {
    slug: 'new-year-countdown-cards',
    category: 'season',
    name: 'New Year countdown cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'hourly', label: 'Hourly cards', subject: 'a sheet of small cards each lettered with an hour such as "7 PM", "8 PM", "9 PM" and a blank activity panel, dashed cut lines'},
        {key: 'clock', label: 'Countdown clock', subject: 'a page with a large clock face to colour and the words "COUNTDOWN" lettered above'},
        {key: 'checklist', label: 'Midnight checklist', subject: 'a page lettered "Before Midnight" with checkboxes and blank lines, a star border'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a vertical page with a gold border and an even margin, the layout centred, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin, the layout centred, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a vertical page with wide margins, the layout centred, print-ready.'},
    ],
    directions: ["for a kids' bedtime countdown", 'counting down from ten', 'for a classroom advent'],
    directionHint: 'try: counting down from ten',
    seo: {
        title: 'New Year Countdown Cards: Hourly, Clock & Checklist',
        metaDescription: 'Nine New Year countdown printables — hourly activity cards, a countdown clock to colour, and a midnight checklist — three festive styles.',
        h1: 'New Year countdown cards: hourly, clock, checklist',
        tagline: 'Nine pieces, three countdown formats, three styles each — lettered, lines left blank.',
        intro: "Three New Year countdown printables for a kids' early countdown: a sheet of hourly activity cards, a clock face to colour, and a midnight checklist, each in black-and-gold, confetti-party, and minimal styles. Ask for a different hour range or an activity swap, and the same countdown grammar carries it.",
        useCases: [
            "a kids' early New Year's Eve countdown",
            'a family countdown party at home',
            'a classroom last-day-of-school countdown',
            'a babysitting-night activity set',
        ],
        faq: [
            {q: 'Are the hourly activities already written in?', a: "The hour prints on each card ('7 PM', '8 PM'...); the activity panel beneath is left blank for you to plan."},
            {q: 'What size do these print at?', a: 'Each piece is a single page sized for cutting into small cards or a standalone sheet.'},
            {q: 'Can I get a different hour range?', a: 'Yes — the direction field takes any variation, including a different hour range.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
